'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useRef, useState, } from 'react';
import { cx } from '../../../internal/cx.js';
import { Button } from '../../actions/button/index.js';
/**
 * Cursor pagination state: accumulates pages, tracks loading, error and end
 * states, ignores stale responses and aborts in-flight requests on reset and
 * unmount. Fetching stays in the product: pass `fetchPage`.
 */
export function useLoadMore(options) {
    const { initialPage, loadOnMount = true } = options;
    const fetchRef = useRef(options.fetchPage);
    useEffect(() => {
        fetchRef.current = options.fetchPage;
    });
    const [items, setItems] = useState(() => initialPage?.items ?? []);
    const [cursor, setCursor] = useState(() => initialPage?.nextCursor ?? undefined);
    const [started, setStarted] = useState(initialPage !== undefined);
    const [status, setStatus] = useState(() => initialPage && (initialPage.nextCursor === null || initialPage.nextCursor === undefined)
        ? 'done'
        : 'idle');
    const [error, setError] = useState(undefined);
    const [total, setTotal] = useState(initialPage?.total);
    const [lastPageCount, setLastPageCount] = useState(initialPage?.items.length ?? 0);
    const request = useRef(null);
    const seq = useRef(0);
    const state = useRef({ cursor, status, started });
    state.current = { cursor, status, started };
    const run = useCallback((from, replace) => {
        request.current?.controller.abort();
        const id = ++seq.current;
        const controller = new AbortController();
        request.current = { id, controller };
        // Synchronously, so a second call in the same tick sees the request in flight.
        state.current = { ...state.current, status: 'loading' };
        setStatus('loading');
        setError(undefined);
        fetchRef.current(from, { signal: controller.signal }).then((page) => {
            if (request.current?.id !== id)
                return;
            request.current = null;
            setItems((prev) => (replace ? page.items : [...prev, ...page.items]));
            setLastPageCount(page.items.length);
            setTotal(page.total);
            const next = page.nextCursor ?? undefined;
            setCursor(next);
            setStarted(true);
            setStatus(next === undefined ? 'done' : 'idle');
        }, (err) => {
            if (request.current?.id !== id || controller.signal.aborted)
                return;
            request.current = null;
            setError(err);
            setStatus('error');
        });
    }, []);
    const loadMore = useCallback(() => {
        const s = state.current;
        if (s.status === 'loading' || s.status === 'done')
            return;
        run(s.started ? s.cursor : undefined, !s.started);
    }, [run]);
    const reset = useCallback(() => {
        setItems([]);
        setCursor(undefined);
        setStarted(false);
        setTotal(undefined);
        setLastPageCount(0);
        state.current = { cursor: undefined, status: 'idle', started: false };
        run(undefined, true);
    }, [run]);
    useEffect(() => {
        if (initialPage === undefined && loadOnMount)
            run(undefined, true);
        return () => request.current?.controller.abort();
        // Mount only: later loads are explicit (loadMore, retry, reset).
    }, []); // eslint-disable-line react-hooks/exhaustive-deps
    const hasMore = status !== 'done';
    return {
        items,
        status,
        error,
        hasMore,
        total,
        lastPageCount,
        loadMore,
        retry: loadMore,
        reset,
        loadMoreProps: {
            hasMore,
            loading: status === 'loading',
            error: status === 'error',
            onLoadMore: loadMore,
            onRetry: loadMore,
            loadedCount: items.length,
            totalCount: total,
        },
    };
}
function plural(n, noun) {
    return n === 1 ? noun.one : noun.other;
}
const DEFAULT_NOUN = { one: 'item', other: 'items' };
/**
 * The footer of a cursor-paginated list: a Load more button with loading,
 * error and retry, end-of-list states and polite announcements, plus
 * optional auto-load on scroll. Pair it with `useLoadMore`, or drive it from
 * any data library through its props.
 */
export const LoadMore = forwardRef(function LoadMore({ hasMore, loading = false, error = false, onLoadMore, onRetry, loadedCount, totalCount, noun = DEFAULT_NOUN, showCount, autoLoad = false, autoLoadMargin = '200px', label = 'Load more', loadingLabel = 'Loading…', errorLabel = 'Couldn’t load more.', retryLabel = 'Retry', endLabel = 'That’s everything', className, ...rest }, ref) {
    const rootRef = useRef(null);
    const buttonRef = useRef(null);
    const endRef = useRef(null);
    const [announcement, setAnnouncement] = useState('');
    const prevCount = useRef(loadedCount);
    const prevError = useRef(error);
    const prevHasMore = useRef(hasMore);
    const buttonHadFocus = useRef(false);
    const setRefs = useCallback((node) => {
        rootRef.current = node;
        if (typeof ref === 'function')
            ref(node);
        else if (ref)
            ref.current = node;
    }, [ref]);
    // Announce what changed: new items, a failure, or the end of the list.
    useEffect(() => {
        const parts = [];
        const before = prevCount.current;
        if (loadedCount !== undefined && before !== undefined && loadedCount > before) {
            const added = loadedCount - before;
            parts.push(`${added} more ${plural(added, noun)} loaded.`);
        }
        if (error && !prevError.current) {
            parts.push(typeof errorLabel === 'string' ? errorLabel : 'Couldn’t load more.');
        }
        if (!hasMore && prevHasMore.current && typeof endLabel === 'string')
            parts.push(endLabel);
        prevCount.current = loadedCount;
        prevError.current = error;
        prevHasMore.current = hasMore;
        if (parts.length)
            setAnnouncement(parts.join(' '));
    }, [loadedCount, error, hasMore, noun, errorLabel, endLabel]);
    // The button disappears at the end; keep keyboard focus on the list footer.
    useEffect(() => {
        if (!hasMore && buttonHadFocus.current) {
            buttonHadFocus.current = false;
            const active = typeof document === 'undefined' ? null : document.activeElement;
            // Only when focus was actually lost with the button, never stealing it from elsewhere.
            if (active === null || active === document.body) {
                (endRef.current ?? rootRef.current)?.focus();
            }
        }
    }, [hasMore]);
    // Auto-load: fire when the footer scrolls into view; the button stays as the fallback.
    useEffect(() => {
        const node = rootRef.current;
        if (!autoLoad || !hasMore || loading || error || !node)
            return;
        if (typeof IntersectionObserver === 'undefined')
            return;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((e) => e.isIntersecting))
                onLoadMore();
        }, { rootMargin: autoLoadMargin });
        observer.observe(node);
        return () => observer.disconnect();
    }, [autoLoad, hasMore, loading, error, onLoadMore, autoLoadMargin]);
    const state = error ? 'error' : loading ? 'loading' : hasMore ? 'idle' : 'done';
    const countVisible = (showCount ?? loadedCount !== undefined) && loadedCount !== undefined;
    const count = countVisible
        ? totalCount !== undefined
            ? `Showing ${loadedCount} of ${totalCount} ${plural(totalCount, noun)}`
            : `Showing ${loadedCount} ${plural(loadedCount, noun)}`
        : null;
    return (_jsxs("div", { ref: setRefs, ...rest, tabIndex: -1, className: cx('mn-load-more', className), "data-state": state, children: [count ? _jsx("p", { className: "mn-load-more__count", children: count }) : null, error ? _jsx("p", { className: "mn-load-more__error", children: errorLabel }) : null, hasMore ? (_jsx(Button, { ref: buttonRef, size: "sm", loading: loading, onFocus: () => (buttonHadFocus.current = true), onBlur: (event) => {
                    // A blur caused by the button unmounting has no related target; keep the flag then.
                    if (event.relatedTarget)
                        buttonHadFocus.current = false;
                }, onClick: () => (error ? (onRetry ?? onLoadMore)() : onLoadMore()), children: loading ? loadingLabel : error ? retryLabel : label })) : endLabel !== null ? (_jsx("p", { ref: endRef, tabIndex: -1, className: "mn-load-more__end", children: endLabel })) : null, _jsx("span", { role: "status", "aria-live": "polite", "aria-atomic": "true", className: "mn-visually-hidden", children: announcement })] }));
});
//# sourceMappingURL=load-more.js.map